import { api } from "./api";

export const trashStatus       = () => api.get("/trash/status").then((r) => r.data);
export const trashAnnouncement = () => api.get("/trash/announcement").then((r) => r.data);
export const trashMissions     = () => api.get("/trash/missions").then((r) => r.data);
export const trashWallet       = () => api.get("/trash/wallet").then((r) => r.data);
export const trashWalletHistory = (limit = 100) => api.get("/trash/wallet/history", { params: { limit } }).then((r) => r.data);
export const trashCashout      = (body) => api.post("/trash/wallet/cashout", body).then((r) => r.data);
export const trashLeaderboard  = (params = {}) => api.get("/trash/leaderboard", { params }).then((r) => r.data);
export const trashProfile      = (uid) => api.get(`/trash/profile/${uid}`).then((r) => r.data);
export const trashUpdateProfile = (body) => api.patch("/trash/me/profile", body).then((r) => r.data);
export const trashBadgesMe     = () => api.get("/trash/badges/me").then((r) => r.data);
export const trashSubscribe    = (action) => api.post("/trash/subscribe", { action }).then((r) => r.data);

/** Scan endpoints — send { image_base64, mime, gps_lat, gps_lon, gps_acc_m, city? } */
export const trashScanBefore   = (body) => api.post("/trash/scan/before", body, { timeout: 60000 }).then((r) => r.data);
export const trashScanAfter    = (body) => api.post("/trash/scan/after", body, { timeout: 60000 }).then((r) => r.data);

// Admin
export const adminTrashDashboard = () => api.get("/admin/trash/dashboard").then((r) => r.data);
export const adminTrashMembers = (params = {}) => api.get("/admin/trash/members", { params }).then((r) => r.data);
export const adminTrashMemberEdit = (uid, body) => api.patch(`/admin/trash/members/${uid}`, body).then((r) => r.data);
export const adminTrashScans = (params = {}) => api.get("/admin/trash/scans", { params }).then((r) => r.data);
export const adminTrashScanAction = (sid, action, note = "") =>
  api.post(`/admin/trash/scans/${sid}/action`, { action, note }).then((r) => r.data);
export const adminTrashPayouts = (status = "pending") => api.get("/admin/trash/payouts", { params: { status } }).then((r) => r.data);
export const adminTrashPayoutAction = (rid, action, note = "") =>
  api.post(`/admin/trash/payouts/${rid}/action`, { action, note }).then((r) => r.data);
export const adminTrashSetAnnouncement = (body) => api.put("/admin/trash/announcement", body).then((r) => r.data);
export const adminTrashSettings = () => api.get("/admin/trash/settings").then((r) => r.data);
export const adminTrashSettingsEdit = (body) => api.patch("/admin/trash/settings", body).then((r) => r.data);

/** Format cents → "$X.XX" */
export const cents = (c) => `$${(Number(c || 0) / 100).toFixed(2)}`;

/** Downscale + orientation-normalize a camera image → JPEG base64. */
export async function downscaleImage(file, { maxSize = 1280, quality = 0.85 } = {}) {
  let bitmap;
  try { bitmap = await createImageBitmap(file, { imageOrientation: "from-image" }); }
  catch {
    const url = URL.createObjectURL(file);
    try {
      bitmap = await new Promise((res, rej) => {
        const img = new Image(); img.onload = () => res(img); img.onerror = rej; img.src = url;
      });
    } finally { setTimeout(() => URL.revokeObjectURL(url), 4000); }
  }
  const srcW = bitmap.width || bitmap.naturalWidth;
  const srcH = bitmap.height || bitmap.naturalHeight;
  const scale = Math.min(1, maxSize / Math.max(srcW, srcH));
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", { alpha: false });
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, w, h);
  const dataUrl = canvas.toDataURL("image/jpeg", quality);
  const base64 = dataUrl.split(",", 2)[1] || "";
  try { bitmap.close?.(); } catch {}
  return { base64, dataUrl, mime: "image/jpeg", width: w, height: h,
           sizeKb: Math.round((base64.length * 3) / 4 / 1024) };
}

/** Wrap navigator.geolocation.getCurrentPosition in a promise with timeout. */
export function getGps(timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) { resolve(null); return; }
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; resolve(null); } }, timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => { if (done) return; done = true; clearTimeout(t);
        resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude, acc: pos.coords.accuracy }); },
      () => { if (done) return; done = true; clearTimeout(t); resolve(null); },
      { enableHighAccuracy: true, maximumAge: 30000, timeout: timeoutMs },
    );
  });
}

/** Standard human-error normalizer for FastAPI structured 422s. */
export function humanErr(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return e?.message || fallback;
}
